import React from 'react';
import { motion } from 'framer-motion';
import { Section, GradientText } from './UI';
import { BookOpen, BrainCircuit, Rocket, DollarSign, ArrowRight } from 'lucide-react';
export function MethodologySection() {
  return <div className="bg-black text-white" data-id="element-177">
      {/* The Problem (Shortened) */}
      <Section className="border-b border-white/10" data-id="element-178">
        <div className="text-center max-w-3xl mx-auto" data-id="element-179">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-180">
            La trampa de la <GradientText data-id="element-181">información infinita.</GradientText>
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-182">
            Has comprado cursos. Has guardado cientos de posts. Has visto horas
            de tutoriales. Sientes que avanzas, pero la realidad es cruda:{' '}
            <strong data-id="element-183">aprender no produce resultados, construir sí.</strong>
          </p>
          <p className="text-xl text-gray-300 font-medium" data-id="element-184">
            El mercado ya no recompensa al que más sabe. Recompensa al que crea
            soluciones.
          </p>
        </div>
      </Section>

      {/* The Transformation */}
      <Section className="bg-white/5" data-id="element-185">
        <div className="text-center mb-16" data-id="element-186">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-187">
            El Cambio de Identidad
          </h2>
          <p className="text-gray-400 text-lg" data-id="element-188">
            De consumir pasivamente a generar valor activamente.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-189">
          <motion.div whileInView={{
          opacity: 1,
          x: 0
        }} initial={{
          opacity: 0,
          x: -20
        }} viewport={{
          once: true
        }} className="bg-red-500/10 border border-red-500/20 rounded-3xl p-8" data-id="element-190">
            <h3 className="text-xl font-bold text-red-400 mb-6 flex items-center gap-2" data-id="element-191">
              ANTES: El Consumidor
            </h3>
            <ul className="space-y-4 text-gray-300" data-id="element-192">
              <li className="flex gap-3" data-id="element-193">
                <span data-id="element-194">❌</span> Acumula PDFs y cursos sin terminar.
              </li>
              <li className="flex gap-3" data-id="element-195">
                <span data-id="element-196">❌</span> Parálisis por análisis. Exceso de ideas.
              </li>
              <li className="flex gap-3" data-id="element-197">
                <span data-id="element-198">❌</span> Síndrome del impostor ("no estoy listo").
              </li>
              <li className="flex gap-3" data-id="element-199">
                <span data-id="element-200">❌</span> Intercambia tiempo por dinero.
              </li>
            </ul>
          </motion.div>

          <motion.div whileInView={{
          opacity: 1,
          x: 0
        }} initial={{
          opacity: 0,
          x: 20
        }} viewport={{
          once: true
        }} className="bg-green-500/10 border border-green-500/20 rounded-3xl p-8 relative overflow-hidden" data-id="element-201">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/20 blur-3xl rounded-full" data-id="element-202"></div>
            <h3 className="text-xl font-bold text-green-400 mb-6 flex items-center gap-2" data-id="element-203">
              DESPUÉS: El Creador
            </h3>
            <ul className="space-y-4 text-gray-300 relative z-10" data-id="element-204">
              <li className="flex gap-3" data-id="element-205">
                <span data-id="element-206">✅</span> Tiene un producto digital terminado y a la
                venta.
              </li>
              <li className="flex gap-3" data-id="element-207">
                <span data-id="element-208">✅</span> Usa la IA como asistente, no como muleta.
              </li>
              <li className="flex gap-3" data-id="element-209">
                <span data-id="element-210">✅</span> Lanza rápido y mejora con feedback real.
              </li>
              <li className="flex gap-3" data-id="element-211">
                <span data-id="element-212">✅</span> Construye activos que generan ingresos pasivos.
              </li>
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* Unique Mechanism */}
      <Section className="py-24" data-id="element-213">
        <div className="text-center mb-20" data-id="element-214">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6" data-id="element-215">
            Presentamos el <GradientText data-id="element-216">Sistema C.R.E.A.™</GradientText>
          </h2>
          <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-217">
            Un marco de trabajo predecible de 4 pasos para extraer tu
            conocimiento, estructurarlo con IA y empaquetarlo en un producto
            listo para vender.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6 relative" data-id="element-218">
          {/* Connecting line for desktop */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-white/5 via-white/20 to-white/5 -translate-y-1/2 z-0" data-id="element-219" />

          {[{
          icon: BookOpen,
          title: 'Concepto',
          desc: 'Valida una idea rentable en un nicho hambriento.'
        }, {
          icon: BrainCircuit,
          title: 'Resolución',
          desc: 'Usa IA para estructurar y redactar el 80% del contenido.'
        }, {
          icon: DollarSign,
          title: 'Empaque',
          desc: 'Diseña una oferta y portada que eleven el valor.'
        }, {
          icon: Rocket,
          title: 'Aceleración',
          desc: 'Lanza con tráfico orgánico y ventas automatizadas.'
        }].map((step, i) => <motion.div key={i} whileInView={{
          opacity: 1,
          y: 0
        }} initial={{
          opacity: 0,
          y: 30
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.15
        }} className="flex-1 relative z-10" data-id="element-220">
              <div className="bg-[#0a0a0a] border border-white/10 rounded-3xl p-8 hover:border-white/30 transition-all duration-300 shadow-2xl h-full flex flex-col items-center text-center group" data-id="element-221">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 border border-yellow-500/30 text-yellow-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-222">
                  <step.icon className="w-8 h-8" data-id="element-223" />
                </div>
                <div className="text-yellow-500 font-bold text-sm tracking-widest mb-2" data-id="element-224">
                  PASO {i + 1}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-225">
                  {step.title}
                </h3>
                <p className="text-gray-400 text-base leading-relaxed" data-id="element-226">
                  {step.desc}
                </p>
              </div>

              {/* Mobile arrow */}
              {i < 3 && <div className="md:hidden flex justify-center py-4 text-white/20" data-id="element-227">
                  <ArrowRight className="w-6 h-6 rotate-90" data-id="element-228" />
                </div>}
            </motion.div>)}
        </div>
      </Section>
    </div>;
}